import React, { useMemo } from 'react';

import { IPersonaFront } from '@api/routers/user/service';
import { useTranslations } from 'next-intl';
import { useBoolean } from 'usehooks-ts';

import { useCurrentCompletedUser } from '@web/features/user/service';
import { Button, IconButton, showSnackbar, Tooltip } from '@web/ui/components';
import { useBulkActions } from '@web/ui/components/BulkAction/useBulkActions';
import { BoostIcon, ClaimIcon, UnstakeIcon } from '@web/ui/icons';

import { useDepositsOrThrow } from '../../hooks';
import { BoostModal } from '../../modals/BoostModal';
import { ClaimModal } from '../../modals/ClaimModal';
import { UnstakeModal } from '../../modals/UnstakeModal';

import EmptyIconStaked from './empty-staked.svg';
import { StakingGrid } from './StakingGrid';
import { StakingPersonasEmpty } from './StakingPersonasEmpty';

export interface IStakedTokensGridProps {
  items: IPersonaFront[];
  onSwitchTab?(): void;
}

export const StakedTokensGrid = ({
  items,
  onSwitchTab,
}: IStakedTokensGridProps) => {
  const completedUser = useCurrentCompletedUser();
  const t = useTranslations();

  const { depositsRecord } = useDepositsOrThrow();

  const {
    value: isClaimModalOpen,
    setTrue: openClaimModal,
    setFalse: closeClaimModal,
  } = useBoolean(false);

  const {
    value: isUnstakeModalOpen,
    setTrue: openUnstakeModal,
    setFalse: closeUnstakeModal,
  } = useBoolean(false);

  const {
    value: isBoostModalOpen,
    setTrue: openBoostModal,
    setFalse: closeBoostModal,
  } = useBoolean(false);

  const { selectedItems, itemClick, clearAll, selectItems } = useBulkActions();

  const availableForBoost = useMemo(
    () =>
      selectedItems.filter(
        (item) => !depositsRecord[`${item.tokenId}`]?.bonusPerDay,
      ),
    [selectedItems, depositsRecord],
  );

  const handleBoostClick = () => {
    if (!completedUser) {
      return;
    }

    if (completedUser.user.boosts < selectedItems.length) {
      showSnackbar(
        t('BULK_ACTION_BOOST_NOT_ENOUGH_BOOSTS', {
          boosts: selectedItems.length,
        }),
        {
          severity: 'error',
        },
      );
      return;
    }

    if (availableForBoost.length === 0) {
      showSnackbar(t('BULK_ACTION_BOOST_ALREADY_BOOSTED'), {
        severity: 'error',
      });

      return;
    }

    openBoostModal();
  };

  return (
    <>
      {items.length > 0 ? (
        <StakingGrid
          items={items}
          selectedItems={selectedItems}
          onSelectAll={() => selectItems(items)}
          onClearAll={clearAll}
          onItemClick={itemClick}
          lockedItems={[]}
          bulkActions={[
            <Tooltip content={t('BULK_ACTION_TOOLTIP_CLAIM')}>
              <IconButton onClick={openClaimModal}>
                <ClaimIcon color="onBackground" />
              </IconButton>
            </Tooltip>,
            <Tooltip content={t('BULK_ACTION_TOOLTIP_UNSTAKE')}>
              <IconButton onClick={openUnstakeModal}>
                <UnstakeIcon color="onBackground" />
              </IconButton>
            </Tooltip>,
            <Tooltip content={t('BULK_ACTION_TOOLTIP_BOOST')}>
              <IconButton onClick={handleBoostClick}>
                <BoostIcon color="onBackground" />
              </IconButton>
            </Tooltip>,
          ]}
        />
      ) : (
        <StakingPersonasEmpty
          icon={<EmptyIconStaked />}
          title={t('STAKING_PERSONAS_STAKED_EMPTY_TITLE')}
          subtitle={t('STAKING_PERSONAS_STAKED_EMPTY_SUBTITLE')}
          action={
            <Button
              label={t('STAKING_PERSONAS_STAKED_EMPTY_ACTION')}
              onClick={onSwitchTab}
            />
          }
        />
      )}

      <ClaimModal
        personas={selectedItems}
        open={isClaimModalOpen}
        onClose={closeClaimModal}
      />

      <UnstakeModal
        personas={selectedItems}
        open={isUnstakeModalOpen}
        onClose={closeUnstakeModal}
      />

      <BoostModal
        personas={availableForBoost}
        open={isBoostModalOpen}
        onClose={closeBoostModal}
      />
    </>
  );
};
